// fallow-ignore-file
import { z } from 'zod';

import { zodAddressSchema } from '@vestigas/data';

import { EmailSchema } from '@vestigas/utils';

import {
  ACTIVATION_REQUEST_STATE,
  ACTIVATION_FEATURE_TYPE,
} from '../constants';

/**
 * Zod schema for the contact-info wrapper used inside `customer` on the public
 * activation view. Mirrors the backend `ContactInfo` model shared with call-off's
 * `issuer_contact`.
 */
const zodPublicViewContactInfoSchema = z.object({
  email: EmailSchema.nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  name: z.string().nullish(),
  phoneNumber: z.string().nullish(),
});

/**
 * Zod schema for the customer party surfaced in the magic-link supplier view.
 */
const zodPublicViewCustomerSchema = z.object({
  address: zodAddressSchema.nullish(),
  contact: zodPublicViewContactInfoSchema.nullish(),
  logoUrl: z.url().nullish(),
  name: z.string().default(''),
});

/**
 * Zod schema for the supplier party surfaced in the magic-link supplier view.
 * Mirrors `PublicViewCustomer` but omits `contact` — the supplier is the one
 * viewing the page.
 */
const zodPublicViewSupplierSchema = z.object({
  address: zodAddressSchema.nullish(),
  logoUrl: z.url().nullish(),
  name: z.string().default(''),
});

/**
 * Zod schema for a single activation request inside the batch
 * (PublicSupplierViewResponse).
 */
const zodSupplierLetterPublicInfoSchema = z.object({
  sasUrl: z.string(),
});

export const zodPublicSupplierViewResponseSchema = z.object({
  customer: zodPublicViewCustomerSchema.nullish(),
  documentType: z.enum(ACTIVATION_FEATURE_TYPE),
  existingCustomerNumbers: z.array(z.string()).default([]),
  id: z.uuid(),
  magicLinkExpiresOn: z.iso.datetime({ offset: true }).nullish(),
  requestDate: z.iso.datetime({ offset: true }).nullish(),
  state: z.union([z.enum(ACTIVATION_REQUEST_STATE), z.string()]),
  supplier: zodPublicViewSupplierSchema.nullish(),
  supplierLetter: zodSupplierLetterPublicInfoSchema.nullish(),
  supplierNote: z.string().nullish(),
});

/**
 * Zod schema for the GET /api/v1/suppliers/activation/{req_id} response
 * (PublicBatchSupplierViewResponse).
 *
 * The batch envelope aggregates every sibling request under one envelope so
 * the FE can render one combined confirm/decline form.
 */
export const zodSupplierActivationPublicResponseSchema = z.object({
  activationBatchId: z.uuid().nullable(),
  customer: zodPublicViewCustomerSchema,
  documentTypes: z.array(z.enum(ACTIVATION_FEATURE_TYPE)),
  existingCustomerNumbers: z.array(z.string()),
  magicLinkExpiresOn: z.iso.datetime({ offset: true }).nullish(),
  requestDate: z.iso.datetime({ offset: true }).nullish(),
  requests: z.array(zodPublicSupplierViewResponseSchema),
  supplier: zodPublicViewSupplierSchema,
  supplierLetter: zodSupplierLetterPublicInfoSchema.nullish(),
  supplierNote: z.string().nullish(),
});
